import type { LocationQuery } from "vue-router";
import type { TAppAbility } from "@/api/models/auth/ability";

export const BILLING_PAGES = [
  "general",
  "invoices",
  "payment-methods",
  "payouts",
  "transactions",
  "intervals",
] as const;
export type TBillingPage = (typeof BILLING_PAGES)[number];

export type TBillingScope = "mso" | "company";
export type TBillingOwnerQuery = Partial<Record<TBillingScope, string>>;

export type TBillingRights = {
  canMsoBilling: boolean;
  canCompanyBilling: boolean;
};

const BILLING_PATH = "/billing";

export const billingPagePath = (page: TBillingPage) => `${BILLING_PATH}/${page}`;

export const isBillingPath = (path: string) =>
  path === BILLING_PATH || path.startsWith(`${BILLING_PATH}/`);

export const readBillingRights = (can: TAppAbility["can"]): TBillingRights => ({
  canMsoBilling: can($permActions.MANAGE_MSO_BILLING, $permSubjects.PRACTICE__MSO),
  canCompanyBilling: can($permActions.MANAGE_BILLING, $permSubjects.PRACTICE__COMPANY),
});

export const canScopeOpenBillingPage = (
  rights: TBillingRights,
  scope: TBillingScope,
  _page: TBillingPage
) => (scope === "mso" ? rights.canMsoBilling : rights.canCompanyBilling);

export const canOpenBillingPage = (rights: TBillingRights, page: TBillingPage) =>
  canScopeOpenBillingPage(rights, "mso", page) || canScopeOpenBillingPage(rights, "company", page);

export const firstQueryValue = (query: LocationQuery, key: string): string | undefined => {
  const value = query[key];
  const first = Array.isArray(value) ? value[0] : value;
  return first || undefined;
};

export const billingScopeOfOwner = (
  rights: TBillingRights,
  owner: TBillingOwnerQuery
): TBillingScope | undefined => {
  if (owner.company && rights.canCompanyBilling) return "company";
  if (owner.mso && rights.canMsoBilling) return "mso";
  if (rights.canMsoBilling !== rights.canCompanyBilling) {
    return rights.canMsoBilling ? "mso" : "company";
  }
  return undefined;
};

export const readBillingOwnerQuery = (query: LocationQuery): TBillingOwnerQuery => {
  const companyId = firstQueryValue(query, "company");
  if (companyId) return { company: companyId };

  const msoId = firstQueryValue(query, "mso");
  return msoId ? { mso: msoId } : {};
};
